import { useState } from 'react';
import {
  DashboardDataTableColumnKind,
  DashboardTableDialogBody,
  DashboardTableDialogContent,
  DashboardTableDialogDataTable,
  DashboardTableDialogFooter,
  DashboardTableDialogHeader,
  buildDashboardDataTableColumns,
} from '@shigo-ui/dashboard-ui';
import { Button, Dialog, Tooltip, TooltipContent, TooltipTrigger } from '@shigo-ui/ui';

import { dashboardT } from '../../../../lib/messages';
import { useChangelogQuery } from '../../hooks';
import {
  ChangelogSortDirection,
  ChangelogSortProperty,
  changelogPageSizes,
  type ChangelogEntry,
} from '../../model';
import {
  changelogFieldLabel,
  displayChangelogValue,
  formatChangelogDate,
} from '../../presentation';

export interface ChangelogDialogScope {
  publisherIds?: string[] | undefined;
  placementIds?: string[] | undefined;
  integrationIds?: string[] | undefined;
  demandPartnerIds?: string[] | undefined;
  createdBy?: string | undefined;
  lineItemIds?: string[] | undefined;
}

interface ChangelogDialogProps {
  title: string;
  description: string;
  scope: ChangelogDialogScope;
  onClose: () => void;
}

const columns = buildDashboardDataTableColumns<ChangelogEntry>([
  {
    id: 'createdOn',
    kind: DashboardDataTableColumnKind.Date,
    header: dashboardT('changelog.date'),
    headerClassName: 'w-56 pl-4',
    className: 'w-56 pl-4 whitespace-nowrap',
    value: (entry) => formatChangelogDate(entry.createdOn),
  },
  {
    id: 'type',
    kind: DashboardDataTableColumnKind.Text,
    header: dashboardT('changelog.type'),
    headerClassName: 'w-28',
    className: 'w-28 font-medium',
    value: (entry) => entry.type,
  },
  {
    id: 'fieldName',
    kind: DashboardDataTableColumnKind.Custom,
    header: dashboardT('changelog.field'),
    headerClassName: 'w-48',
    className: 'w-48',
    cell: (entry) => <ChangelogValue value={changelogFieldLabel(entry)} />,
  },
  {
    id: 'oldValue',
    kind: DashboardDataTableColumnKind.Custom,
    header: dashboardT('changelog.oldValue'),
    headerClassName: 'w-64',
    className: 'w-64',
    cell: (entry) => <ChangelogValue value={displayChangelogValue(entry.oldValue)} />,
  },
  {
    id: 'arrow',
    kind: DashboardDataTableColumnKind.Text,
    header: '',
    headerClassName: 'w-10 text-center',
    className: 'w-10 text-center',
    value: () => '→',
  },
  {
    id: 'newValue',
    kind: DashboardDataTableColumnKind.Custom,
    header: dashboardT('changelog.newValue'),
    headerClassName: 'w-64',
    className: 'w-64',
    cell: (entry) => <ChangelogValue value={displayChangelogValue(entry.newValue)} />,
  },
  {
    id: 'createdBy',
    kind: DashboardDataTableColumnKind.Custom,
    header: dashboardT('changelog.user'),
    headerClassName: 'w-56 pr-4',
    className: 'w-56 pr-4',
    cell: (entry) => <ChangelogValue value={entry.createdBy ?? '-'} />,
  },
]);

export function ChangelogDialog({ title, description, scope, onClose }: ChangelogDialogProps) {
  const [pagination, setPagination] = useState({ page: 0, pageSize: 10 });
  const query = useChangelogQuery({
    publisherIds: scope.publisherIds ?? [],
    placementIds: scope.placementIds ?? [],
    integrationIds: scope.integrationIds ?? [],
    demandPartnerIds: scope.demandPartnerIds ?? [],
    createdBy: scope.createdBy ?? '',
    lineItemIds: scope.lineItemIds ?? [],
    dateFrom: '',
    dateTo: '',
    page: pagination.page,
    size: pagination.pageSize,
    sortProperty: ChangelogSortProperty.CreatedOn,
    sortDirection: ChangelogSortDirection.Desc,
  });

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DashboardTableDialogContent className="max-w-[min(1500px,calc(100vw-2rem))]">
        <DashboardTableDialogHeader title={title} description={description} />
        <DashboardTableDialogBody className="flex flex-col overflow-hidden">
          <DashboardTableDialogDataTable
            fillHeight
            className="min-h-0 flex-1"
            tableLabel={title}
            columns={columns}
            items={query.data?.content ?? []}
            getRowKey={(entry) => String(entry.id)}
            loading={query.isFetching}
            loadingMessage={dashboardT('changelog.loading')}
            emptyMessage={dashboardT('changelog.empty')}
            errorMessage={query.isError ? dashboardT('changelog.loadError') : undefined}
            minWidthClassName="min-w-[1168px]"
            pagination={{
              value: {
                page: pagination.page,
                pageSize: pagination.pageSize,
                totalItems: query.data?.totalElements ?? 0,
              },
              pageSizes: changelogPageSizes,
              disabled: query.isFetching,
              onChange: (next) => {
                setPagination({
                  page: next.pageSize === pagination.pageSize ? next.page : 0,
                  pageSize: next.pageSize,
                });
              },
            }}
          />
        </DashboardTableDialogBody>
        <DashboardTableDialogFooter>
          <Button
            type="button"
            variant="outline"
            aria-label={dashboardT('changelog.closeDialog')}
            onClick={onClose}
          >
            {dashboardT('changelog.close')}
          </Button>
        </DashboardTableDialogFooter>
      </DashboardTableDialogContent>
    </Dialog>
  );
}

function ChangelogValue({ value }: { value: string }) {
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <button
          className="focus-visible:ring-ring block w-full truncate rounded-sm text-left focus-visible:ring-2 focus-visible:outline-none"
          type="button"
        >
          {value}
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-96 break-words whitespace-pre-wrap">{value}</TooltipContent>
    </Tooltip>
  );
}
